import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Stack,
  Button,
  Container,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
  LinearProgress,
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';

// ---------------------------------------------------------------------------
// Batches of ONE program, with a side-by-side quiz comparison per cohort.
// Each row opens that batch's dedicated report.
//
// The comparison comes from GET /quiz-attempts/batch-comparison?eventId=…,
// which groups attempts by the batch stamped on them at submit time. Attempts
// with no batch (submitted before batches existed, or by a student in no batch)
// come back as a single batchId:null row shown here as "Unassigned".
// ---------------------------------------------------------------------------

type Row = {
  batchId: string | null;
  batchName: string;
  attempts: number;
  students: number;
  quizzes: number;
  avgPercentage: number;
  passRate: number;
  lastAt?: string;
};

export default function ProgramBatches() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [program, setProgram] = useState<any>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axiosInstance.get(APIS.EVENTS.BY_ID(eventId)).then((r) => r.data?.data ?? r.data),
      axiosInstance
        .get(APIS.QUIZ_ATTEMPTS.BATCH_COMPARISON, { params: { eventId } })
        .then((r) => r.data?.data ?? r.data),
    ])
      .then(([ev, cmp]) => {
        if (cancelled) return;
        setProgram(ev);
        setRows(Array.isArray(cmp) ? cmp : []);
      })
      .catch((e: any) => {
        if (cancelled) return;
        enqueueSnackbar(e?.message || 'Failed to load program batches', { variant: 'error' });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId, enqueueSnackbar]);

  const batches = (program?.batches || []) as any[];
  const title = program?.title || program?.name || 'Program';

  // Quiz stats keyed by batch id, so configured batches with zero attempts
  // still render a row (rather than silently vanishing).
  const statsById = useMemo(() => {
    const m = new Map<string, Row>();
    rows.forEach((r) => m.set(r.batchId ? String(r.batchId) : '__none__', r));
    return m;
  }, [rows]);

  const unassigned = statsById.get('__none__');

  const merged = useMemo(
    () =>
      batches.map((b: any) => {
        const id = String(b._id);
        const s = statsById.get(id);
        return {
          _id: id,
          name: b.name || '—',
          students: (b.students || []).length,
          active: b.active !== false,
          startDate: b.startDate,
          endDate: b.endDate,
          attempts: s?.attempts ?? 0,
          quizzes: s?.quizzes ?? 0,
          avgPercentage: s?.avgPercentage ?? 0,
          passRate: s?.passRate ?? 0,
          submitters: s?.students ?? 0,
        };
      }),
    [batches, statsById]
  );

  const fmtDate = (d?: string) => (d ? String(d).slice(0, 10) : '—');

  return (
    <Page title={`${title} — Batches`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`${title} — Batches`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Programs', href: PATH_DASHBOARD.events },
            { name: 'Batches' },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-back-outline" />}
              onClick={() => navigate(PATH_DASHBOARD.events)}
            >
              Back to programs
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Every batch sees the same lectures and quizzes. Batches group students so quiz results
            can be compared per cohort. Add or edit batches by editing the program.
          </Typography>
        </Card>

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Batch</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell align="center">Students</TableCell>
                  <TableCell align="center">Quizzes taken</TableCell>
                  <TableCell align="center">Attempts</TableCell>
                  <TableCell align="center">Average</TableCell>
                  <TableCell align="center">Pass rate</TableCell>
                  <TableCell align="right">Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && merged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        This program has no batches yet. Edit the program to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {merged.map((b) => (
                  <TableRow key={b._id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">{b.name}</Typography>
                        {!b.active && <Label color="default">Inactive</Label>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(b.startDate)} → {fmtDate(b.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{b.students}</TableCell>
                    <TableCell align="center">{b.quizzes}</TableCell>
                    <TableCell align="center">{b.attempts}</TableCell>
                    <TableCell align="center">
                      {b.attempts ? (
                        <Box sx={{ minWidth: 90 }}>
                          <Typography variant="subtitle2">{b.avgPercentage}%</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, b.avgPercentage)}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {b.attempts ? (
                        <Label color={b.passRate >= 50 ? 'success' : 'warning'}>
                          {b.passRate}%
                        </Label>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          navigate(PATH_DASHBOARD.programBatchReport(eventId, b._id))
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Attempts that carry no batch — kept visible so pre-batch
                    history is never silently dropped from the totals. */}
                {unassigned && unassigned.attempts > 0 && (
                  <TableRow hover sx={{ bgcolor: 'background.neutral' }}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">Unassigned</Typography>
                        <Label color="warning">No batch</Label>
                      </Stack>
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="center">—</TableCell>
                    <TableCell align="center">{unassigned.quizzes}</TableCell>
                    <TableCell align="center">{unassigned.attempts}</TableCell>
                    <TableCell align="center">{unassigned.avgPercentage}%</TableCell>
                    <TableCell align="center">
                      <Label color="default">{unassigned.passRate}%</Label>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(PATH_DASHBOARD.programBatchReport(eventId, 'none'))}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Page>
  );
}
